<template>
  <div v-if="!isFooterOnly">
    <form @submit.prevent="handleSubmit">
      <div class="field">
        <label class="label">Assign order to *</label>
        <div class="control">
          <label class="radio">
            <input type="radio" v-model="assignToOwner" :value="false" />
            Myself (admin)
          </label>
          <label class="radio ml-4">
            <input type="radio" v-model="assignToOwner" :value="true" />
            Company owner
          </label>
        </div>
      </div>

      <div class="field">
        <label class="checkbox">
          <input type="checkbox" v-model="form.visible_for_users" />
          Visible for users
        </label>
      </div>

      <div class="field">
        <label class="label">Company *</label>
        <div class="control">
          <searchable-select
            endpoint="companies"
            label-key="name"
            placeholder="Search company..."
            @select="onCompanySelect"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Project *</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select
              v-model="selectedProjectId"
              :disabled="!selectedCompany"
              required
            >
              <option value="">
                {{ selectedCompany ? "Select project" : "Select a company first" }}
              </option>
              <option v-for="project in projects" :key="project.id" :value="project.id">
                {{ project.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">Camera *</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select
              v-model="selectedCameraId"
              :disabled="!selectedProjectId"
              required
            >
              <option value="">
                {{ selectedProjectId ? "Select camera" : "Select a project first" }}
              </option>
              <option v-for="camera in cameras" :key="camera.id" :value="camera.id">
                {{ camera.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="field is-grouped">
        <div class="control is-expanded">
          <label class="label">Start Date *</label>
          <input type="date" class="input" v-model="startDate" required />
        </div>
        <div class="control">
          <label class="label">Start Hour *</label>
          <input type="time" class="input" v-model="startHour" required />
        </div>
      </div>

      <div class="field is-grouped">
        <div class="control is-expanded">
          <label class="label">End Date *</label>
          <input type="date" class="input" v-model="endDate" required />
        </div>
        <div class="control">
          <label class="label">End Hour *</label>
          <input type="time" class="input" v-model="endHour" required />
        </div>
      </div>

      <div class="field">
        <label class="checkbox">
          <input type="checkbox" v-model="form.remove_blur" />
          Remove blur
        </label>
      </div>

      <div class="field">
        <label class="label">Format</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select v-model="form.format">
              <option value="">None</option>
              <option value="youtube">YouTube</option>
              <option value="youtube-4k">YouTube 4K</option>
              <option value="youtube-shorts">YouTube Shorts</option>
              <option value="instagram-reels">Instagram Reels</option>
              <option value="instagram-post-square">Instagram Post (Square)</option>
              <option value="instagram-post-portrait">Instagram Post (Portrait)</option>
              <option value="tiktok">TikTok</option>
              <option value="facebook">Facebook</option>
              <option value="facebook-reels">Facebook Reels</option>
              <option value="linkedin">LinkedIn</option>
              <option value="twitter">Twitter</option>
              <option value="pinterest">Pinterest</option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">Filter</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select v-model="form.filter">
              <option value="">None</option>
              <option value="warm">Warm</option>
              <option value="cool">Cool</option>
              <option value="vivid">Vivid</option>
              <option value="cinematic">Cinematic</option>
              <option value="dramatic">Dramatic</option>
              <option value="golden-hour">Golden Hour</option>
              <option value="vintage">Vintage</option>
              <option value="bw">Black &amp; White</option>
              <option value="noir">Noir</option>
              <option value="sepia">Sepia</option>
              <option value="cyberpunk">Cyberpunk</option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">Motion Blur</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select v-model="motionBlurMode">
              <option value="none">None</option>
              <option value="blur">Blur</option>
              <option value="blur-high">Blur (High)</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>
      </div>
      <div class="field" v-if="motionBlurMode === 'custom'">
        <label class="label">Motion Blur Strength</label>
        <div class="control">
          <input
            type="number"
            class="input"
            min="2"
            max="12"
            v-model.number="form.motion_blur_strength"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Zoom</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select v-model="form.zoom">
              <option value="">None</option>
              <option value="in">In</option>
              <option value="out">Out</option>
              <option value="in-out">In-Out</option>
            </select>
          </div>
        </div>
      </div>
      <div class="field is-grouped" v-if="form.zoom">
        <div class="control is-expanded">
          <label class="label">Zoom Amount</label>
          <input
            type="number"
            step="0.1"
            min="1"
            max="3"
            class="input"
            v-model.number="form.zoom_amount"
          />
        </div>
        <div class="control is-expanded">
          <label class="label">Zoom Duration (s)</label>
          <input
            type="number"
            min="5"
            max="60"
            class="input"
            v-model.number="form.zoom_duration"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Pan</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select v-model="form.pan">
              <option value="">None</option>
              <option value="left-to-right">Left to Right</option>
              <option value="right-to-left">Right to Left</option>
            </select>
          </div>
        </div>
      </div>
      <div class="field is-grouped" v-if="form.pan">
        <div class="control is-expanded">
          <label class="label">Pan Amount</label>
          <input
            type="number"
            step="0.05"
            min="1.1"
            max="3"
            class="input"
            v-model.number="form.pan_amount"
          />
        </div>
        <div class="control is-expanded">
          <label class="label">Pan Duration (s)</label>
          <input
            type="number"
            min="5"
            max="60"
            class="input"
            v-model.number="form.pan_duration"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Duration (s, 0 = auto)</label>
        <div class="control">
          <input
            type="number"
            min="0"
            max="300"
            step="5"
            class="input"
            v-model.number="form.duration"
          />
        </div>
      </div>

      <div class="field">
        <p class="help">* Required fields</p>
      </div>
    </form>
  </div>

  <div v-else class="buttons">
    <button class="button" @click="closeModal">Cancel</button>
    <button class="button is-primary" @click="triggerSubmit">
      Create Video Order
    </button>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { httpInstance as http } from "@/plugins/http";
import toast from "@/utils/toast";
import SearchableSelect from "@/components/SearchableSelect.vue";

export default {
  name: "ModalAddVideoOrder",
  components: {
    "searchable-select": SearchableSelect,
  },
  props: {
    modalData: {
      type: Object,
      default: () => ({}),
    },
    modalIndex: {
      type: Number,
      required: true,
    },
    isFooterOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const today = new Date();
    const toDateInput = (date) => date.toISOString().slice(0, 10);

    return {
      assignToOwner: false,
      selectedCompany: null,
      projects: [],
      selectedProjectId: "",
      cameras: [],
      selectedCameraId: "",
      startDate: toDateInput(today),
      endDate: toDateInput(today),
      startHour: "08:00",
      endHour: "18:00",
      motionBlurMode: "none",
      form: {
        remove_blur: false,
        visible_for_users: true,
        format: "",
        filter: "",
        motion_blur_strength: 3,
        zoom: "",
        zoom_amount: 1.3,
        zoom_duration: 20,
        pan: "",
        pan_amount: 1.5,
        pan_duration: 30,
        duration: 0,
      },
    };
  },
  methods: {
    ...mapActions(["closeTopModal"]),
    closeModal() {
      this.closeTopModal();
    },
    triggerSubmit() {
      this.$root.$emit("add-video-order-submit");
    },
    async onCompanySelect(company) {
      this.selectedCompany = company;
      this.projects = [];
      this.selectedProjectId = "";
      this.cameras = [];
      this.selectedCameraId = "";
      if (!company) return;

      try {
        const response = await http.get("projects", {
          params: { company_id: company.id, page: 1, size: 50 },
        });
        this.projects = response.data.data || [];
      } catch (error) {
        console.error("Error fetching projects:", error);
        toast.error("Error fetching projects");
      }
    },
    async fetchCameras(projectId) {
      this.cameras = [];
      this.selectedCameraId = "";
      if (!projectId) return;

      try {
        const response = await http.get("list-all-cameras-of-project", {
          params: { project_id: projectId },
        });
        this.cameras = response.data || [];
      } catch (error) {
        console.error("Error fetching cameras:", error);
        toast.error("Error fetching cameras");
      }
    },
    async handleSubmit() {
      if (!this.selectedCameraId) {
        toast.error("Please select company, project and camera");
        return;
      }
      if (!this.startDate || !this.endDate || !this.startHour || !this.endHour) {
        toast.error("Please fill all required fields");
        return;
      }

      const transitionMap = {
        none: "",
        blur: "motion-blur",
        "blur-high": "motion-blur",
        custom: "motion-blur",
      };
      const motionBlurStrengthMap = {
        none: 0,
        blur: 3,
        "blur-high": 8,
        custom: this.form.motion_blur_strength,
      };

      const payload = {
        camera_id: this.selectedCameraId,
        assign_to_owner: this.assignToOwner,
        visible_for_users: this.form.visible_for_users,
        start_date: Math.floor(new Date(this.startDate).getTime() / 1000),
        end_date: Math.floor(new Date(this.endDate).getTime() / 1000),
        start_hour: this.startHour,
        end_hour: this.endHour,
        remove_blur: this.form.remove_blur,
        format: this.form.format || undefined,
        filter: this.form.filter || undefined,
        transition: transitionMap[this.motionBlurMode] || undefined,
        motion_blur_strength: motionBlurStrengthMap[this.motionBlurMode] || undefined,
        zoom: this.form.zoom || undefined,
        zoom_amount: this.form.zoom ? this.form.zoom_amount : undefined,
        zoom_duration: this.form.zoom ? this.form.zoom_duration : undefined,
        pan: this.form.pan || undefined,
        pan_amount: this.form.pan ? this.form.pan_amount : undefined,
        pan_duration: this.form.pan ? this.form.pan_duration : undefined,
        duration: this.form.duration > 0 ? this.form.duration : undefined,
      };

      try {
        const response = await http.post("video-orders", payload);
        if (response.status === 200 || response.status === 201) {
          toast.success("Video order created successfully");
          this.closeModal();
          this.$root.$emit("video-order-created");
        }
      } catch (error) {
        console.error("Error creating video order:", error);
        toast.error("Error creating video order");
      }
    },
  },
  watch: {
    selectedProjectId(newProjectId) {
      this.fetchCameras(newProjectId);
    },
  },
  mounted() {
    if (!this.isFooterOnly) {
      this.$root.$on("add-video-order-submit", this.handleSubmit);
    }
  },
  beforeDestroy() {
    if (!this.isFooterOnly) {
      this.$root.$off("add-video-order-submit", this.handleSubmit);
    }
  },
};
</script>

<style scoped>
.buttons {
  justify-content: flex-end;
}
</style>
